import React, { createContext, useContext, useState, useEffect } from 'react';

/*
 * ThemeContext — dark (default) and light (WR-015 §2).
 *
 * WR-013 removed this file when Bastion was dark-only; WR-015 reverses that
 * decision, so it is back — but for two themes, not the six it used to carry.
 *
 * Three deliberate behaviours:
 *
 *   1. Dark is the default. Bastion is dark-first by identity; light is
 *      opt-in.
 *   2. `prefers-color-scheme` is NOT consulted. A user whose OS is light does
 *      not get a light Bastion unless they ask for one. Stated in the WR.
 *   3. Dark sets NO attribute — it is the :root default — so the DOM in the
 *      default case is exactly what WR-013 shipped, and dark cannot drift.
 *
 * The first paint is handled before React runs, by the inline script in
 * public/index.html. This provider keeps the attribute in step afterwards;
 * it is not what avoids the flash.
 */

export const THEMES = [
  { id: 'dark',  name: 'Dark'  },
  { id: 'light', name: 'Light' },
];

const VALID = new Set(THEMES.map(t => t.id));
export const DEFAULT_THEME = 'dark';
export const STORAGE_KEY = 'bastion-theme';

/** Read the stored choice. Shared with the pre-paint script in index.html. */
export function storedTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && VALID.has(v) ? v : DEFAULT_THEME;
  } catch {
    // Private mode, blocked storage — fall back to the default rather than
    // failing to render.
    return DEFAULT_THEME;
  }
}

function applyTheme(theme) {
  if (theme === DEFAULT_THEME) {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(storedTheme);

  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* non-fatal */ }
  }, [theme]);

  const selectTheme = (id) => { if (VALID.has(id)) setTheme(id); };
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, themes: THEMES, selectTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext) || {
  // Soft default for anything rendered outside the provider.
  theme: DEFAULT_THEME, themes: THEMES, selectTheme: () => {}, toggleTheme: () => {},
};
